/**
 * Les icônes d'Hermès.
 *
 * Dessinées, jamais des emoji ni des glyphes système : un seul trait, une seule
 * graisse, un seul jeu de terminaisons. Elles héritent toutes de la couleur du
 * texte — aucune ne porte sa propre teinte, sinon la discipline des trois
 * familles de couleur tomberait au premier bouton.
 *
 * Le jeu est volontairement plus fourni que celui de `src/ui/` : cette
 * direction s'appuie davantage sur des boutons ronds sans libellé (appeler,
 * garder), qui n'ont que l'icône pour se faire comprendre. Chacun porte donc
 * aussi un `aria-label` côté appelant.
 */

import type { ReactNode } from 'react';

interface IconProps {
  className?: string;
  size?: number;
}

function Frame({
  className,
  size = 20,
  strokeWidth = 1.7,
  children,
}: IconProps & { strokeWidth?: number; children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <Frame {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </Frame>
  );
}

export function IconLibrary(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10v16H5.5A1.5 1.5 0 0 1 4 18.5z" />
      <path d="M10 4h8.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H10" />
      <path d="M14 8.5h3M14 12h3" />
    </Frame>
  );
}

export function IconCalendar(props: IconProps) {
  return (
    <Frame {...props}>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
    </Frame>
  );
}

export function IconPin(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M12 21s7-6.2 7-11.6A7 7 0 0 0 5 9.4C5 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.4" r="2.6" />
    </Frame>
  );
}

export function IconPhone(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M7.8 3.8 9.9 7.5 8 9.9c.9 2.1 2.5 3.7 4.6 4.6l2.4-1.9 3.7 2.1v3.6c0 1-.9 1.8-1.9 1.7C9.6 19.2 4.8 14.4 4 7.2c-.1-1 .7-1.9 1.7-1.9z" />
    </Frame>
  );
}

export function IconBookmark(props: IconProps & { filled?: boolean }) {
  const { filled = false, ...rest } = props;
  if (filled) {
    return (
      <svg
        className={props.className}
        width={props.size ?? 20}
        height={props.size ?? 20}
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="currentColor"
      >
        <path d="M6.5 3h11a1.5 1.5 0 0 1 1.5 1.5V21l-7-4.2L5 21V4.5A1.5 1.5 0 0 1 6.5 3z" />
      </svg>
    );
  }
  return (
    <Frame {...rest}>
      <path d="M6.5 3h11a1.5 1.5 0 0 1 1.5 1.5V21l-7-4.2L5 21V4.5A1.5 1.5 0 0 1 6.5 3z" />
    </Frame>
  );
}

export function IconStar({ className, size = 12 }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 12 12"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M6 0l1.7 3.7 4 .5-3 2.8.8 4L6 9l-3.5 2 .8-4-3-2.8 4-.5z" />
    </svg>
  );
}

export function IconChevron(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="m9 5 7 7-7 7" />
    </Frame>
  );
}

export function IconArrow(props: IconProps) {
  return (
    <Frame {...props} strokeWidth={1.8}>
      <path d="M4 12h16M14 6l6 6-6 6" />
    </Frame>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <Frame {...props} strokeWidth={2.2}>
      <path d="M5 12.5l4.2 4.3L19 7" />
    </Frame>
  );
}

export function IconClose(props: IconProps) {
  return (
    <Frame {...props} strokeWidth={1.8}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Frame>
  );
}

export function IconTrash(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M4.5 6.5h15M9.5 6.5V4.5h5v2M6.5 6.5l.9 12.3c.05.7.6 1.2 1.3 1.2h6.6c.7 0 1.25-.5 1.3-1.2l.9-12.3" />
    </Frame>
  );
}

export function IconMail(props: IconProps) {
  return (
    <Frame {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="m3 6.5 9 6.3 9-6.3" />
    </Frame>
  );
}

export function IconLock(props: IconProps) {
  return (
    <Frame {...props}>
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M7.8 10.5V7.5a4.2 4.2 0 0 1 8.4 0v3" />
    </Frame>
  );
}

export function IconShop(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M3.5 9h17M3.5 9 6 4h12l2.5 5M3.5 9v10a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5V9" />
      <path d="M9 13h6" />
    </Frame>
  );
}

export function IconGlobe(props: IconProps) {
  return (
    <Frame {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.2 2.4 3.3 5.3 3.3 8.5s-1.1 6.1-3.3 8.5c-2.2-2.4-3.3-5.3-3.3-8.5S9.8 5.9 12 3.5z" />
    </Frame>
  );
}

export function IconGauge(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M4 17a8.5 8.5 0 1 1 16 0" />
      <path d="m12 12.5 3.6-3" />
      <circle cx="12" cy="13.5" r="1.2" fill="currentColor" stroke="none" />
    </Frame>
  );
}

export function IconRefresh(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4.5V10h-5.5" />
    </Frame>
  );
}

export function IconDownload(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </Frame>
  );
}

export function IconExit(props: IconProps) {
  return (
    <Frame {...props}>
      <path d="M15 4.5h3A1.5 1.5 0 0 1 19.5 6v12a1.5 1.5 0 0 1-1.5 1.5h-3" />
      <path d="M4.5 12h10M11 8.5l3.5 3.5-3.5 3.5" />
    </Frame>
  );
}

/**
 * La tasse fumante : l'état vide et l'attente.
 *
 * Ce n'est pas une icône de barre d'onglets — elle a sa propre taille et sa
 * propre animation — donc elle ne passe pas par `Frame`.
 */
export function CupMark({ size = 86, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={(size / 86) * 86}
      viewBox="0 0 86 86"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M18 38h40v14a16 16 0 0 1-16 16H34a16 16 0 0 1-16-16z"
        fill="#ebd7be"
        stroke="#b08e6c"
        strokeWidth="2"
      />
      <path d="M58 42h6a7 7 0 0 1 0 14h-6" stroke="#b08e6c" strokeWidth="2" />
      <path
        d="M31 30c0-4 4-4 4-8s-4-4-4-8"
        stroke="#c8a07a"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M45 30c0-4 4-4 4-8s-4-4-4-8"
        stroke="#c8a07a"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
